// src/pages/Moderation/UserViolationsDetailsPage/UserViolationsDetailsPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getModerationReportsRequest } from 'redux/reducers/moderation/reducer';
import { getModerationReportsSelector } from 'redux/reducers/moderation/selectors';
import type { GetReportsResponse, ReportInfo } from 'redux/reducers/moderation/types';

import { Loader } from 'components/Loader';
import { Link } from 'react-router-dom';
import { base } from 'routes/ModerationRoutes';
import { fetchWithAuthentication } from 'untils/getFile';
import { getProtectedImageBlob } from 'untils/imageCache';
import { onHttp } from 'untils/onHttp';
import { safeJsonParse } from 'untils/safeJsonParse';
import './UserViolationsDetailsPage.scss';

type UiMedia = {
  kind: 'photo' | 'video' | 'gif' | 'document' | 'audio' | 'unknown';
  url?: string;
  thumbUrl?: string;
  fileName?: string;
};

type UiContent = {
  text?: string;
  url?: string;
  media: UiMedia[];
};

const formatDate = (dt?: string | null) => {
  if (!dt) return '—';
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return dt;
  return d.toLocaleString();
};

const badgeClassBySeverity = (sev?: string | null) => {
  if (sev === 'high') return 'badge badge--danger';
  if (sev === 'medium') return 'badge badge--warning';
  if (sev === 'low') return 'badge badge--info';
  return 'badge';
};

const getReporterDisplayName = (reporter: ReportInfo['user'] | undefined) => {
  if (!reporter) return '—';
  if (reporter.username) return `@${reporter.username}`;
  const fullName = `${reporter.first_name ?? ''} ${reporter.last_name ?? ''}`.trim();
  return fullName.length ? fullName : '—';
};

const buildMediaUrl = (type: 'photo' | 'document', accessHash?: string) => {
  if (!accessHash) return undefined;
  return `${onHttp()}/storage/${type}/${accessHash}`;
};

const extractReportContent = (reportInfo: ReportInfo): UiContent => {
  const textFromMessage = reportInfo?.message?.message ?? '';
  const messageDataRaw = reportInfo?.message?.message_data ?? '';

  const uiContent: UiContent = {
    text: textFromMessage || undefined,
    url: undefined,
    media: [],
  };

  if (!messageDataRaw) return uiContent;

  let parsed: any = null;
  try {
    parsed = safeJsonParse(messageDataRaw);
  } catch {
    return uiContent;
  }

  // URL (якщо є)
  const urlCandidate = parsed?.link ?? parsed?.url ?? parsed?.webpage?.url ?? undefined;

  if (urlCandidate) {
    uiContent.url = urlCandidate;
  }

  const mediaBlock = parsed?.media;

  // ============================
  // PHOTO
  // ============================
  const photo = mediaBlock?.photo_FLAGPHOTO;
  if (photo?.access_hash) {
    const photoUrl = buildMediaUrl('photo', photo.access_hash);

    uiContent.media.push({
      kind: 'photo',
      url: photoUrl,
      thumbUrl: photoUrl,
      fileName: 'photo',
    });
    uiContent.url = photoUrl;
  }

  // ============================
  // DOCUMENT / VIDEO / GIF
  // ============================
  const document = mediaBlock?.document;

  if (document?.access_hash) {
    const mime: string | undefined = document?.mime_type;

    const isVideo = mime?.startsWith('video/');
    const isGif = mime === 'image/gif';
    const isImage = mime?.startsWith('image/');

    const documentUrl = buildMediaUrl('document', document.access_hash);

    const kind: UiMedia['kind'] = isVideo
      ? 'video'
      : isGif
      ? 'gif'
      : isImage
      ? 'photo'
      : 'document';

    uiContent.media.push({
      kind,
      url: documentUrl,
      thumbUrl: documentUrl,
      fileName: document?.attributes?.find((a: any) => a?.file_name)?.file_name ?? 'document',
    });
  }

  return uiContent;
};

export const UserViolationsDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [mediaSrc, setMediaSrc] = useState<string>();
  const [mediaLoading, setMediaLoading] = useState(false);
  const reportsState = useSelector(getModerationReportsSelector) as GetReportsResponse | null;

  const [page, setPage] = useState(1);
  const perPage = 10;

  const [selectedReportInfo, setSelectedReportInfo] = useState<ReportInfo | null>(null);

  const numericUserId = useMemo(() => {
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  }, [id]);

  useEffect(() => {
    if (!numericUserId) return;

    dispatch(
      getModerationReportsRequest({
        current_page: page,
        per_page: perPage,
        user_id: numericUserId,
        has_restriction: true,
      }),
    );
  }, [dispatch, numericUserId, page]);

  const reports: ReportInfo[] = Array.isArray(reportsState?.data) ? reportsState!.data : [];
  const total: number = typeof reportsState?.count === 'number' ? reportsState.count : 0;

  useEffect(() => {
    if (!reports.length) {
      setSelectedReportInfo(null);
      return;
    }

    // якщо selected немає або його вже нема в новому списку
    const stillExists = reports.find((r) => r.report.id === selectedReportInfo?.report?.id);

    if (!stillExists) {
      setSelectedReportInfo(reports[0]);
    }
  }, [reports]);

  const canNext = reports.length === perPage;

  const selectedContent = selectedReportInfo ? extractReportContent(selectedReportInfo) : null;

  const firstMedia = selectedContent?.media?.[0];

  const severityFromReport = selectedReportInfo?.report?.restricted
    ? 'high'
    : selectedReportInfo?.report?.approved === false
    ? 'medium'
    : selectedReportInfo?.report?.approved === true
    ? 'low'
    : null;

  useEffect(() => {
    if (!firstMedia?.url) {
      setMediaSrc(undefined);
      return;
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('token') || '' : '';

    let alive = true;
    let objectUrl: string | undefined;

    getProtectedImageBlob(firstMedia.url, token, fetchWithAuthentication, setMediaLoading)
      .then((blob) => {
        if (!alive) return;

        objectUrl = URL.createObjectURL(blob);
        setMediaSrc(objectUrl);
      })
      .catch(() => {
        if (alive) setMediaSrc(undefined);
      });

    return () => {
      alive = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [firstMedia?.url]);

  useEffect(() => {
    if (!firstMedia?.url) {
      setMediaSrc(undefined);
      return;
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('token') || '' : '';

    let alive = true;
    let objectUrl: string | undefined;

    getProtectedImageBlob(firstMedia.url, token, fetchWithAuthentication, setMediaLoading)
      .then((blob) => {
        if (!alive) return;

        objectUrl = URL.createObjectURL(blob);
        setMediaSrc(objectUrl);
      })
      .catch(() => {
        if (alive) setMediaSrc(undefined);
      });

    return () => {
      alive = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [firstMedia?.url]);

  return (
    <div className="ch-violations">
      <div className="ch-violations__header card">
        <div className="ch-violations__header-left">
          <div className="ch-violations__title">User violations</div>
          <div className="ch-violations__subtitle">
            User ID: <span className="mono">{numericUserId ?? '—'}</span>
          </div>
        </div>

        <div className="ch-violations__header-right">
          <div className="stat">
            <div className="stat__label">Total</div>
            <div className="stat__value">{total}</div>
          </div>
          <div className="stat">
            <div className="stat__label">Page</div>
            <div className="stat__value">
              {page} <span className="muted">/</span> {Math.max(1, Math.ceil(total / perPage))}
            </div>
          </div>
        </div>
      </div>

      <div className="ch-violations__grid">
        {/* LEFT */}
        <div className="card">
          <div className="card__head">
            <div className="card__title">Reports</div>
            <div className="card__meta muted">Click row to preview content</div>
          </div>

          <div className="table">
            <div className="table__row table__row--head">
              <div className="table__cell">Report ID</div>
              <div className="table__cell">Peer</div>
              <div className="table__cell">Moderation</div>
              <div className="table__cell">Created</div>
              <div className="table__cell">User</div>
            </div>

            {reports.map((reportInfo: ReportInfo) => {
              const isActiveRow = selectedReportInfo?.report?.id === reportInfo.report.id;

              const reporterDisplayName = getReporterDisplayName(reportInfo.user);
              const createdAt = reportInfo.report?.created_at;
              const peerLabel = `${reportInfo.report?.peer_type ?? '—'} / ${
                reportInfo.report?.peer_id ?? '—'
              }`;

              const rowSeverity = reportInfo.report?.restricted
                ? 'high'
                : reportInfo.report?.approved === false
                ? 'medium'
                : reportInfo.report?.approved === true
                ? 'low'
                : null;

              const moderationLabel =
                reportInfo.report?.approved === true
                  ? 'Approved'
                  : reportInfo.report?.approved === false
                  ? 'Rejected'
                  : 'Pending';

              return (
                <button
                  key={reportInfo.report.id}
                  type="button"
                  className={`table__row ${isActiveRow ? 'is-active' : ''}`}
                  onClick={() => setSelectedReportInfo(reportInfo)}
                >
                  <div className="table__cell mono">#{reportInfo.report.id}</div>

                  <div className="table__cell">{peerLabel}</div>

                  <div className="table__cell">
                    <span className={badgeClassBySeverity(rowSeverity)}>{moderationLabel}</span>
                  </div>

                  <div className="table__cell">{formatDate(createdAt)}</div>

                  <div className="table__cell">{reporterDisplayName}</div>
                </button>
              );
            })}

            {!reports.length ? <div className="empty">No reports</div> : null}
          </div>

          <div className="pager">
            <button
              className="btn btn--ghost"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>

            <div className="pager__info mono">{page}</div>

            <button
              className="btn btn--ghost"
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>

        {/* RIGHT */}
        <div className="card">
          <div className="card__head">
            <div className="card__title">Content</div>
            <div className="card__meta muted">Preview</div>
          </div>

          {!selectedReportInfo ? (
            <div className="empty">Select report</div>
          ) : (
            <div className="preview">
              <div className="preview__meta">
                <div className="preview__row">
                  <span className="label">Report</span>
                  <span className="mono">#{selectedReportInfo.report.id}</span>
                </div>

                <div className="preview__row">
                  <span className="label">Peer</span>
                  <span className="mono">
                    {selectedReportInfo.report.peer_type} / {selectedReportInfo.report.peer_id}
                  </span>
                </div>

                <div className="preview__row">
                  <span className="label">Severity</span>
                  <span className={badgeClassBySeverity(severityFromReport)}>
                    {severityFromReport ?? '—'}
                  </span>
                </div>

                <div className="preview__row">
                  <span className="label">Created</span>
                  <span>{formatDate(selectedReportInfo.report.created_at)}</span>
                </div>

                <div className="preview__row">
                  <span className="label">Restricted</span>
                  <span>{selectedReportInfo.report.restricted ? 'Yes' : 'No'}</span>
                </div>

                <div className="preview__row">
                  <span className="label">Restriction</span>
                  <span className="mono">{selectedReportInfo.report.restriction ?? '—'}</span>
                </div>
                <div className="preview__row">
                  <span className="label">Go to Report</span>
                  <Link
                    className="uv-table__open"
                    to={`${base}/details-queue/${selectedReportInfo.report.id}`}
                  >
                    Open
                  </Link>
                </div>
              </div>

              <div className="divider" />

              <div className="preview__content">
                {selectedContent?.text ? (
                  <div className="block">
                    <div className="label">Text</div>
                    <div className="pre">{selectedContent.text}</div>
                  </div>
                ) : null}

                {/* {selectedContent?.url ? (
                  <div className="block">
                    <div className="label">URL</div>
                    <a className="link" href={selectedContent.url} target="_blank" rel="noreferrer">
                      {selectedContent.url}
                    </a>
                  </div>
                ) : null} */}
                {firstMedia ? (
                  <div className="block">
                    <div className="label">Media</div>
                    <div className="media">
                      {mediaLoading ? (
                        <Loader />
                      ) : firstMedia.kind === 'video' ? (
                        <video
                          className="pm-card__img"
                          src={mediaSrc}
                          controls
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img
                          className="pm-card__img"
                          src={mediaSrc}
                          alt={firstMedia.fileName}
                          loading="lazy"
                        />
                      )}
                    </div>
                  </div>
                ) : null}

                {!selectedContent?.text &&
                !selectedContent?.url &&
                !selectedContent?.media?.length ? (
                  <div className="empty">No content payload</div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
